import { useContext, useEffect, useRef, useState } from 'react'
import type { AccessScope } from '../../types/schema'
import type { WorkspaceRole } from '../../types/workspace'
import { Button } from '../Common'
import { TimelineContext } from '../../context/timelineContextCore'
import { useNls } from '../../nls/nlsProvider'
import {
  saveMemberAccessScope,
  subscribeToMemberAccessScope,
} from '../../services/accessScopeService'
import type { TimelineElement } from '../../types/timelineElement'
import { AccessScopeEditor } from './AccessScopeEditor'
import './accessScopePanel.css'

export interface AccessScopePanelProps {
  workspaceId: string
  member: {
    uid: string
    displayName?: string
    role?: WorkspaceRole | string
  }
  canManage: boolean
}

function isScopeEqual(a: AccessScope | null, b: AccessScope | null): boolean {
  if (!a || !b) return a === b
  if (a.mode !== b.mode) return false
  const aGrants = a.grants ?? {}
  const bGrants = b.grants ?? {}
  const aKeys = Object.keys(aGrants)
  const bKeys = Object.keys(bGrants)
  if (aKeys.length !== bKeys.length) return false
  for (const k of aKeys) {
    if (aGrants[k] !== bGrants[k]) return false
  }
  return true
}

const EMPTY_ELEMENTS: TimelineElement[] = []

export const AccessScopePanel = ({
  workspaceId,
  member,
  canManage,
}: AccessScopePanelProps) => {
  const { t } = useNls()
  const timeline = useContext(TimelineContext)
  const elements = timeline?.elements ?? EMPTY_ELEMENTS

  const role = (member.role ?? 'VIEWER') as WorkspaceRole
  const isHelper = role === 'HELPER'
  const isOwnerOrAdmin = role === 'OWNER' || role === 'ADMIN'
  const shouldSubscribe = Boolean(workspaceId && member.uid && !isHelper && !isOwnerOrAdmin)

  const [loading, setLoading] = useState(shouldSubscribe)
  const [savedScope, setSavedScope] = useState<AccessScope | null>(null)
  const [draftScope, setDraftScope] = useState<AccessScope | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const savedScopeRef = useRef<AccessScope | null>(null)
  const tRef = useRef(t)

  useEffect(() => {
    savedScopeRef.current = savedScope
  }, [savedScope])

  useEffect(() => {
    tRef.current = t
  }, [t])

  useEffect(() => {
    if (!shouldSubscribe || !workspaceId || !member.uid) {
      return
    }

    const unsubscribe = subscribeToMemberAccessScope(
      workspaceId,
      member.uid,
      (scope) => {
        const prevSaved = savedScopeRef.current
        savedScopeRef.current = scope
        setSavedScope(scope)
        setDraftScope((current) => {
          // If the user already has dirty changes, keep the draft
          if (current && prevSaved && !isScopeEqual(current, prevSaved)) {
            return current
          }
          return scope
        })
        setLoading(false)
      },
      (err) => {
        setErrorMessage(err.message || tRef.current('NLSAccessScopeLoadFailed'))
        setLoading(false)
      },
    )

    return () => {
      unsubscribe()
    }
  }, [workspaceId, member.uid, shouldSubscribe])

  if (isHelper) {
    return null
  }

  if (isOwnerOrAdmin) {
    return (
      <div className="accessScopePanel">
        <div className="accessScopeHeader">
          <h4 className="accessScopeTitle">{t('NLSAccessScopeSectionTitle')}</h4>
        </div>
        <p className="accessScopeOwnerAdminNote">{t('NLSAccessScopeOwnerAdminNote')}</p>
      </div>
    )
  }

  if (errorMessage && !draftScope) {
    return (
      <div className="accessScopePanel">
        <div className="accessScopeHeader">
          <h4 className="accessScopeTitle">{t('NLSAccessScopeSectionTitle')}</h4>
        </div>
        <div className="accessScopeError" role="alert">
          {errorMessage}
        </div>
      </div>
    )
  }

  if (loading || !draftScope) {
    return (
      <div className="accessScopePanel">
        <div className="accessScopeHeader">
          <h4 className="accessScopeTitle">{t('NLSAccessScopeSectionTitle')}</h4>
        </div>
        <div className="accessScopeLoading">{t('NLSAccessScopeLoading')}</div>
      </div>
    )
  }

  const isDirty = !isScopeEqual(draftScope, savedScope)

  const handleDiscard = () => {
    if (!canManage || isSaving || !savedScope) return
    setDraftScope(savedScope)
    setSaveStatus(null)
    setErrorMessage(null)
  }

  const handleSave = async () => {
    if (!canManage || isSaving || !draftScope) return
    setIsSaving(true)
    setSaveStatus(null)
    setErrorMessage(null)

    try {
      await saveMemberAccessScope(workspaceId, member.uid, draftScope)
      setIsSaving(false)
      setSaveStatus(t('NLSAccessScopeSaved'))
      savedScopeRef.current = draftScope
      setSavedScope(draftScope)
    } catch (err: unknown) {
      setIsSaving(false)
      const errObj = err as Error
      setErrorMessage(errObj.message || t('NLSAccessScopeSaveFailed'))
      // Rejected call keeps draft
    }
  }

  return (
    <div className="accessScopePanel">
      <div className="accessScopeHeader">
        <h4 className="accessScopeTitle">{t('NLSAccessScopeSectionTitle')}</h4>
      </div>

      <AccessScopeEditor
        value={draftScope}
        onChange={(next) => {
          setSaveStatus(null)
          setErrorMessage(null)
          setDraftScope(next)
        }}
        role={role}
        elements={elements}
        disabled={!canManage || isSaving}
      />

      {errorMessage && (
        <div className="accessScopeError" role="alert">
          {errorMessage}
        </div>
      )}

      {saveStatus && (
        <div className="accessScopeStatus" role="status">
          {saveStatus}
        </div>
      )}

      {isDirty && canManage && (
        <div className="accessScopeActions">
          <Button
            variant="primary"
            size="sm"
            loading={isSaving}
            disabled={isSaving}
            onClick={() => void handleSave()}
          >
            {isSaving ? t('NLSAccessScopeActionSaving') : t('NLSAccessScopeActionSave')}
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            disabled={isSaving}
            onClick={handleDiscard}
          >
            {t('NLSAccessScopeActionDiscard')}
          </Button>
        </div>
      )}
    </div>
  )
}
export default AccessScopePanel
